import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CloudCog, AlertTriangle, RefreshCw } from "lucide-react";

const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const SRC_COLOR = { upload: "bg-emerald-500", watchdog: "bg-rose-500", function_check: "bg-amber-500", test: "bg-sky-500", app: "bg-slate-400" };

export function GoogleCloudCostCard() {
  const [data, setData] = useState(null);
  const [budget, setBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(null);

  const loadPreview = async (kind) => {
    try { setPreview((await apiClient.get("/admin/google-costs/report-preview", { params: { kind } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
  };
  const sendReport = async (kind) => {
    setSending(kind);
    try {
      const r = await apiClient.post("/admin/google-costs/send-report", null, { params: { kind } });
      toast.success(r.data.sent ? `${kind === "monthly" ? "Monatsbericht" : "Tagesbericht"} an alle Super-Admins gesendet (Glocke, Push, E-Mail)` : "Bericht erstellt – kein SMTP konfiguriert, nur In-App zugestellt");
    } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    finally { setSending(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/google-costs", { params: { days: 14 } });
      setData(r.data);
      setBudget(String(r.data.budget_chf));
    } catch (e) { toast.error(e.response?.data?.detail || "Google-Kosten konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveBudget = async () => {
    try {
      await apiClient.put("/admin/google-costs/budget", { daily_budget_chf: parseFloat(budget) });
      toast.success("Tagesbudget gespeichert – der Integrations-Wächter warnt bei Überschreitung");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  if (!data) return null;
  const max = Math.max(0.01, ...data.days.map((d) => d.cost_chf));
  const total14 = data.days.reduce((s, d) => s + d.cost_chf, 0);
  const bySrc = {};
  data.days.forEach((d) => Object.entries(d.by_source).forEach(([k, v]) => { bySrc[k] = (bySrc[k] || 0) + v.cost_chf; }));
  const labels = data.source_labels || {};

  return (
    <Card data-testid="google-cost-card" className={data.over_budget ? "border-rose-300" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CloudCog className="h-5 w-5 text-sky-600" /> Google Cloud Kosten (Document AI + Vision)
          <Badge variant="outline" className={`ml-2 ${data.over_budget ? "border-rose-300 bg-rose-50 text-rose-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`} data-testid="google-cost-today">
            heute {chf(data.today_cost_chf)} / Budget {chf(data.budget_chf)}
          </Badge>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={load} disabled={loading} data-testid="google-cost-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.over_budget && (
          <div className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800" data-testid="google-cost-over-budget">
            <AlertTriangle className="h-4 w-4" /> Tagesbudget überschritten – Verursacher unten prüfen.
          </div>
        )}
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
            <span>Letzte 14 Tage · total {chf(total14)} · laufender Monat {chf(data.month_cost_chf_partial)}</span>
            <span>Preis: Invoice Parser ${data.price_docai_usd.toFixed(2)}/Dokument (je 10 Seiten) · Vision ${data.price_vision_usd}/Aufruf · 1 USD = {data.usd_to_chf} CHF</span>
          </div>
          <div className="flex h-28 items-end gap-1" data-testid="google-cost-chart">
            {data.days.map((d) => (
              <div key={d.date} className="group relative flex h-full flex-1 flex-col justify-end" title={`${d.date}: ${chf(d.cost_chf)} · ${d.docai_docs} Dokumente · ${d.vision_calls} Vision`}>
                <div className="flex w-full flex-col-reverse overflow-hidden rounded-t" style={{ height: `${Math.max(2, (d.cost_chf / max) * 100)}%` }}>
                  {Object.entries(d.by_source).map(([k, v]) => (
                    <div key={k} className={SRC_COLOR[k] || "bg-slate-400"} style={{ height: `${(v.cost_chf / Math.max(0.0001, d.cost_chf)) * 100}%` }} />
                  ))}
                  {!Object.keys(d.by_source).length && <div className="h-full bg-slate-200" />}
                </div>
                <div className={`mt-1 text-center text-[10px] ${d.cost_chf > data.budget_chf ? "font-semibold text-rose-700" : "text-slate-400"}`}>{d.date.slice(8)}.</div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-md border border-slate-200 p-3 text-sm" data-testid="google-cost-sources">
            <div className="mb-1 font-medium text-slate-800">Verursacher (14 Tage)</div>
            {Object.keys(bySrc).length === 0 && <div className="text-xs text-slate-400">Keine Google-Aufrufe</div>}
            {Object.entries(bySrc).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 py-0.5">
                <span className={`h-2.5 w-2.5 rounded-sm ${SRC_COLOR[k] || "bg-slate-400"}`} />
                <span className="flex-1">{labels[k] || k}</span>
                <span className="font-mono text-xs">{chf(v)}</span>
              </div>
            ))}
            <div className="mt-2 text-[11px] text-slate-500">Heute: {data.today.docai_docs} Document-AI-Dokumente · {data.today.vision_calls} Vision-Aufrufe · {data.today.errors} Fehler</div>
          </div>
          <div className="rounded-md border border-slate-200 p-3 text-sm">
            <div className="mb-1 font-medium text-slate-800">Tagesbudget-Warnung</div>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.5" min="0.5" className="w-28" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="google-budget-input" />
              <span className="text-xs text-slate-500">CHF / Tag</span>
              <Button size="sm" onClick={saveBudget} data-testid="google-budget-save">Speichern</Button>
            </div>
            <div className="mt-3 mb-1 font-medium text-slate-800">Kosten-Mail an Super-Admins</div>
            <div className="flex flex-wrap items-center gap-2" data-testid="cost-report-actions">
              <Button variant="outline" size="sm" onClick={() => loadPreview("daily")} data-testid="cost-report-preview-daily">Vorschau Tagesbericht</Button>
              <Button variant="outline" size="sm" onClick={() => loadPreview("monthly")} data-testid="cost-report-preview-monthly">Vorschau Monatsbericht</Button>
              <Button size="sm" onClick={() => sendReport("daily")} disabled={!!sending} data-testid="cost-report-send-daily">Jetzt senden</Button>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">Automatisch: täglich 07:00 (Vortag) und am 1. des Monats 07:05 (kompletter Vormonat) – per E-Mail, Glocke und Push.</div>
            {preview && (
              <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2" data-testid="cost-report-preview">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-slate-800">{preview.subject}</span>
                  <button type="button" className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setPreview(null)}>schliessen</button>
                </div>
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-slate-700">{preview.body}</pre>
              </div>
            )}
            <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[11px] text-slate-500">
              <li>Integrations-Wächter prüft Google alle 10 Min <b>gratis</b> (Zugangsdaten/Prozessor); kostenpflichtiger Testbeleg höchstens 1×/24 h.</li>
              <li>Testsuite / Firmen «ABNAHME-…» (oder Flag <code>ai_test_mode</code>) nutzen keine kostenpflichtige Google-OCR.</li>
              <li>Historische Aufrufe wurden auf den echten Preis ($0.10/Dokument) umgerechnet.</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
